import React from 'react';
import { Plus, Server } from 'lucide-react';
import { InnerLayout } from '../components/layout/InnerLayout';
import { Button } from '../components/ui/Button';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { useNotificationProviders } from './rules/hooks/useNotificationProviders';
import { ProviderCard } from './rules/components/ProviderCard';
import { AddProviderModal } from './rules/components/AddProviderModal';

export const RulesView: React.FC = () => {
  const {
    providers,
    isLoading,
    providerModalOpen,
    setProviderModalOpen,
    openAddProviderModal,
    providerType,
    setProviderType,
    providerName,
    setProviderName,
    smtpHost,
    setSmtpHost,
    smtpPort,
    setSmtpPort,
    smtpMode,
    setSmtpMode,
    smtpUsername,
    setSmtpUsername,
    smtpPassword,
    setSmtpPassword,
    smtpFrom,
    setSmtpFrom,
    smtpFromName,
    setSmtpFromName,
    showSmtpPassword,
    setShowSmtpPassword,
    isTestingSmtp,
    smtpTestResult,
    resendApiKey,
    setResendApiKey,
    resendFrom,
    setResendFrom,
    isSavingProvider,
    providerToDelete,
    setProviderToDelete,
    isDeletingProvider,
    testingCardId,
    cardTestStatus,
    handleTestSmtp,
    handleTestExistingProvider,
    handleSaveProvider,
    handleDeleteProvider,
    executeDeleteProvider,
    canAdmin,
  } = useNotificationProviders();

  return (
    <InnerLayout
      header={{
        title: 'Notification Providers',
        description:
          'Configure Bring-Your-Own-Provider email dispatchers for automated notifications.',
        actions: canAdmin
          ? [
              {
                label: 'Add Provider',
                icon: Plus,
                variant: 'primary',
                onClick: openAddProviderModal,
              },
            ]
          : undefined,
      }}
    >
      {/* Providers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {providers.map((p) => (
          <ProviderCard
            key={p.id}
            provider={p}
            cardTest={cardTestStatus[p.id]}
            isTesting={testingCardId === p.id}
            canAdmin={canAdmin}
            onTest={() => handleTestExistingProvider(p)}
            onDelete={() => handleDeleteProvider(p)}
          />
        ))}

        {providers.length === 0 && !isLoading && (
          <div className="col-span-full py-16 text-center text-cf-textSecondary bg-cf-surface border border-cf-border rounded-lg p-6">
            <Server size={32} className="mx-auto text-cf-textMuted mb-3 opacity-60" />
            <h3 className="text-sm font-semibold text-cf-textPrimary">No Providers Configured</h3>
            <p className="text-xs text-cf-textSecondary mt-1 max-w-md mx-auto">
              Bring your own SMTP server or Resend API credentials to start sending automated notifications.
            </p>
            {canAdmin && (
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                <Button
                  variant="primary"
                  size="xs"
                  leftIcon={<Plus size={13} />}
                  onClick={() => {
                    setProviderType('smtp');
                    setProviderModalOpen(true);
                  }}
                >
                  Configure SMTP
                </Button>
                <Button
                  variant="secondary"
                  size="xs"
                  leftIcon={<Plus size={13} />}
                  onClick={() => {
                    setProviderType('resend');
                    setProviderModalOpen(true);
                  }}
                >
                  Configure Resend
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Provider Modal */}
      <AddProviderModal
        isOpen={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        providerType={providerType}
        setProviderType={setProviderType}
        providerName={providerName}
        setProviderName={setProviderName}
        smtpHost={smtpHost}
        setSmtpHost={setSmtpHost}
        smtpPort={smtpPort}
        setSmtpPort={setSmtpPort}
        smtpMode={smtpMode}
        setSmtpMode={setSmtpMode}
        smtpUsername={smtpUsername}
        setSmtpUsername={setSmtpUsername}
        smtpPassword={smtpPassword}
        setSmtpPassword={setSmtpPassword}
        smtpFrom={smtpFrom}
        setSmtpFrom={setSmtpFrom}
        smtpFromName={smtpFromName}
        setSmtpFromName={setSmtpFromName}
        showSmtpPassword={showSmtpPassword}
        setShowSmtpPassword={setShowSmtpPassword}
        isTestingSmtp={isTestingSmtp}
        smtpTestResult={smtpTestResult}
        resendApiKey={resendApiKey}
        setResendApiKey={setResendApiKey}
        resendFrom={resendFrom}
        setResendFrom={setResendFrom}
        isSavingProvider={isSavingProvider}
        onTestSmtp={handleTestSmtp}
        onSubmit={handleSaveProvider}
      />

      {/* Delete Provider Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(providerToDelete)}
        onClose={() => setProviderToDelete(null)}
        onConfirm={executeDeleteProvider}
        title="Delete Notification Provider"
        message={
          <>
            Are you sure you want to remove connector{' '}
            <strong className="text-cf-textPrimary font-semibold">
              {providerToDelete?.name}
            </strong>
            ? Any active datastore notification rules bound to this provider will fail delivery
            until reassigned.
          </>
        }
        confirmLabel="Delete Provider"
        isLoading={isDeletingProvider}
      />
    </InnerLayout>
  );
};
